import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import {
    useDeleteDistributorCompanyMutation,
    useDeleteUserMutation,
    useGetUserQuery,
    useImpersonateUserMutation,
    useUpdateUserStatusMutation,
} from 'api/main';
import { userSelectors } from 'reducers';
import { notificationDialogOpen } from 'reducers/appNotification';
import { getUserIdFromIri, parseFullName, saveInStorage } from 'utils/helpers';
import { userAvailabilityStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import PortfolioDialog from 'containers/investors-accounts/PortfolioDialog';
import RoleChangeDialog from 'containers/role-changing/RoleChangeDialog';
import {
    SubscriptionMoveDialog,
    SubscriptionTransferDialog,
    Loader,
    ConfirmDialog,
} from 'components';
import { MenuItem, ListItemText, Link } from '@mui/material';
import * as icons from 'mdi-material-ui';

export const MenuDeleteUser = ({ classes = {}, id, type = 'user' }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteUser, { isSuccess: deleteUserSuccessful, isLoading: deleteUserLoader }] =
        useDeleteUserMutation();
    const [deleteDistributorCompany, { isLoading: deleteLoader, isSuccess: deleteSuccessful }] =
        useDeleteDistributorCompanyMutation();

    const impersonatedByUserId = useMemo(
        () =>
            internalUser?.impersonatedByUser
                ? getUserIdFromIri(internalUser.impersonatedByUser)
                : null,
        [internalUser]
    );

    const handleConfirm = useCallback(() => {
        if (type === 'distributor') {
            deleteDistributorCompany(id);
        } else {
            deleteUser(id);
        }
    }, [deleteDistributorCompany, deleteUser, id, type]);

    useEffect(() => {
        if (deleteSuccessful || deleteUserSuccessful) {
            setDeleteOpen(false);
        }
    }, [deleteSuccessful, deleteUserSuccessful]);

    return (
        <MenuItem
            classes={{ root: classes.menuItem }}
            onClick={() => setDeleteOpen(true)}
            disabled={
                // cannot delete yourself or the user that is currently impersonating
                id === internalUser.id || id === impersonatedByUserId
            }
        >
            <ConfirmDialog
                open={deleteOpen}
                title={texts.SUPPRIMER}
                loading={deleteLoader || deleteUserLoader}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleConfirm}
            >
                {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
            </ConfirmDialog>
            <ListItemText disableTypography classes={{ root: classes.itemText }}>
                <icons.Close className={classes.actionIcons} /> {texts.SUPPRIMER}
            </ListItemText>
        </MenuItem>
    );
};

export const MenuDelete = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.Close className={classes.actionIcons} /> {texts.SUPPRIMER}
        </ListItemText>
    </MenuItem>
);

export const MenuEdit = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.Pencil className={classes.actionIcons} /> {texts.EDIT}
        </ListItemText>
    </MenuItem>
);

export const MenuSubstitute = ({
    classes = {},
    user,
    itemText,
    disabled,
    originalPath,
    followupPath,
    connectAs,
}) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const impersonatedByUserId = useSelector(userSelectors.getInternalUser)?.impersonatedByUser;

    const [impersonateOpen, setImpersonateOpen] = useState(false);

    const [impersonateUser, { isLoading: impersonateLoader }] = useImpersonateUserMutation({
        fixedCacheKey: 'user-impersionation',
    });

    const handleConfirmImpersonate = useCallback(() => {
        impersonateUser({ userId: user.id, followupPath });
    }, [user, impersonateUser, followupPath]);

    const handleImpersonateOpen = useCallback(
        e => {
            e.stopPropagation();
            setImpersonateOpen(true);
            saveInStorage('route', originalPath);
        },
        [originalPath]
    );

    const handleClose = useCallback(e => {
        e.stopPropagation();
        setImpersonateOpen(false);
    }, []);

    return (
        <MenuItem
            classes={{ root: classes.menuItem }}
            onClick={handleImpersonateOpen}
            disabled={
                disabled ||
                user.id === internalUser.id ||
                !!impersonatedByUserId || // cannot chain impersonate
                user.availabilityStatus === userAvailabilityStatus.INACTIVE
            }
        >
            {!!user && (
                <ConfirmDialog
                    open={impersonateOpen}
                    loading={impersonateLoader}
                    title={
                        connectAs ? texts.CONNECT_AS(connectAs) : texts.CONNEXION_EN_SUBSTITUTION
                    }
                    handleClose={handleClose}
                    handleConfirm={handleConfirmImpersonate}
                >
                    {texts.VOULOIR_VOUS_CONNECTER} {parseFullName(user) + '\xa0?'}
                </ConfirmDialog>
            )}
            <ListItemText disableTypography classes={{ root: classes.itemText }}>
                <icons.AccountSwitch className={classes.actionIcons} /> {itemText}
            </ListItemText>
        </MenuItem>
    );
};

export const MenuSendMessage = ({ classes = {}, receiver }) => {
    const dispatch = useDispatch();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const impersonatedByUserId = useSelector(userSelectors.getInternalUser)?.impersonatedByUser;

    const handleNotificationDialog = useCallback(() => {
        dispatch(notificationDialogOpen(receiver));
    }, [receiver, dispatch]);

    return (
        <MenuItem
            classes={{ root: classes.menuItem }}
            onClick={handleNotificationDialog}
            disabled={receiver.id === internalUser.id || receiver.id === impersonatedByUserId}
        >
            <ListItemText disableTypography classes={{ root: classes.itemText }}>
                <icons.Send className={classes.actionIcons} /> {texts.ENVOYER_UN_MESSAGE}
            </ListItemText>
        </MenuItem>
    );
};

export const MenuSendSMS = ({
    classes = {},
    id,
    currentUserId,
    impersonatedByUserId,
    onSendSMS,
    disabled,
}) => (
    <MenuItem
        classes={{ root: classes.menuItem }}
        onClick={onSendSMS}
        disabled={disabled || id === currentUserId || id === impersonatedByUserId}
    >
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.Message className={classes.actionIcons} /> {texts.ENVOYER_UN_SMS}
        </ListItemText>
    </MenuItem>
);

export const MenuDetails = ({ classes = {}, path = '/' }) => (
    <MenuItem classes={{ root: classes.menuItem }}>
        <Link
            href={path}
            to={path}
            component={RouterLink}
            sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'inherit',
                textDecoration: 'none',
                width: '100%',
            }}
        >
            <icons.AccountDetails className={classes.actionIcons} /> {texts.DETAILS}
        </Link>
    </MenuItem>
);

export const MenuPath = ({ classes = {}, path = '/', label, Icon, ...rest }) => (
    <MenuItem classes={{ root: classes.menuItem }} {...rest}>
        <Link
            href={path}
            to={path}
            component={RouterLink}
            sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'inherit',
                textDecoration: 'none',
                width: '100%',
            }}
        >
            <Icon className={classes.actionIcons} /> {label}
        </Link>
    </MenuItem>
);

export const MenuReopen = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.LockOpenVariant className={classes.actionIcons} /> {texts.REOPEN_TUNNEL}
        </ListItemText>
    </MenuItem>
);

export const MenuDeactivate = ({
    classes = {},
    id,
    currentUserId,
    impersonatedByUserId,
    status,
}) => {
    const [updateUserStatus, { isLoading }] = useUpdateUserStatusMutation();
    const handleUpdateStatus = useCallback(
        status => () => {
            updateUserStatus({ id, status });
        },
        [id, updateUserStatus]
    );

    return (
        <MenuItem
            classes={{ root: classes.menuItem }}
            onClick={handleUpdateStatus(
                status === userAvailabilityStatus.ACTIVE
                    ? userAvailabilityStatus.INACTIVE
                    : userAvailabilityStatus.ACTIVE
            )}
            disabled={
                status === userAvailabilityStatus.PENDING_PASSWORD_CHANGE ||
                isLoading ||
                id === currentUserId ||
                id === impersonatedByUserId
            }
        >
            <ListItemText disableTypography classes={{ root: classes.itemText }}>
                <Loader loading={isLoading} size={25} marginTop={0} color="inherit">
                    <icons.AccountReactivate className={classes.actionIcons} />{' '}
                    {status === userAvailabilityStatus.INACTIVE ? texts.ACTIVER : texts.DESACTIVER}
                </Loader>
            </ListItemText>
        </MenuItem>
    );
};

export const MenuChangeRole = ({
    classes = {},
    user,
    userSubscription,
    isServicer,
    isProvider,
    isPromoter,
}) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const impersonatedByUserId = useSelector(userSelectors.getInternalUser)?.impersonatedByUser;
    const [dialogOpen, setDialogOpen] = useState(false);

    return (
        <React.Fragment>
            <RoleChangeDialog
                open={dialogOpen}
                userSubscription={userSubscription}
                onClose={() => setDialogOpen(false)}
                isServicer={isServicer}
                isProvider={isProvider}
                isPromoter={isPromoter}
            />
            <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={() => setDialogOpen(true)}
                disabled={user.id === internalUser.id || user.id === impersonatedByUserId}
            >
                <ListItemText disableTypography classes={{ root: classes.itemText }}>
                    <icons.AccountMultipleOutline className={classes.actionIcons} />{' '}
                    {texts.CHANGE_ROLE}
                </ListItemText>
            </MenuItem>
        </React.Fragment>
    );
};

export const MenuDownload = ({ classes = {}, download, downloading }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={downloading ? null : download}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={downloading} size={25} marginTop={0} color="inherit">
                <icons.DownloadCircle className={classes.actionIcons} /> {texts.GENERER_PEC}
            </Loader>
        </ListItemText>
    </MenuItem>
);

export const MenuSignBS = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.Pen className={classes.actionIcons} /> {texts.CHOOSE_SIGNER}
        </ListItemText>
    </MenuItem>
);

export const MenuSubscriptionTransfer = ({ classes = {}, maxAdmissionFee, subscriptionId }) => {
    const [subscriptionTransferOpen, setSubscriptionTransferOpen] = useState(false);

    return (
        <React.Fragment>
            <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={() => setSubscriptionTransferOpen(true)}
            >
                <ListItemText disableTypography classes={{ root: classes.itemText }}>
                    <icons.Share className={classes.actionIcons} /> {texts.TRANSFER_TO_SUBSCRIBER}
                </ListItemText>
            </MenuItem>
            <SubscriptionTransferDialog
                open={subscriptionTransferOpen}
                handleClose={() => setSubscriptionTransferOpen(false)}
                maxAdmissionFee={maxAdmissionFee}
                subscriptionId={subscriptionId}
            />
        </React.Fragment>
    );
};

export const MenuSubscriptionMove = ({ classes = {}, subscriptionId }) => {
    const [subscriptionMoveOpen, setSubscriptionMoveOpen] = useState(false);

    return (
        <React.Fragment>
            <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={() => setSubscriptionMoveOpen(true)}
            >
                <ListItemText disableTypography classes={{ root: classes.itemText }}>
                    <icons.Share className={classes.actionIcons} /> {texts.TRANSFER}
                </ListItemText>
            </MenuItem>
            <SubscriptionMoveDialog
                open={subscriptionMoveOpen}
                handleClose={() => setSubscriptionMoveOpen(false)}
                subscriptionId={subscriptionId}
            />
        </React.Fragment>
    );
};

export const MenuMessageDetails = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.MessageText className={classes.actionIcons} /> {texts.DETAILS}
        </ListItemText>
    </MenuItem>
);

export const MenuNewIndirectSubscription = ({ classes = {}, onClick }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.AccountPlus className={classes.actionIcons} /> {texts.CREATE_SUBSCRIPTION}
        </ListItemText>
    </MenuItem>
);

export const MenuSimulate = ({ loading, classes, ...props }) => (
    <MenuItem classes={{ root: classes.menuItem }} {...props}>
        <Loader size={20} marginTop={0} loading={loading} color="inherit">
            <icons.FileExcel className={classes.actionIcons} /> {texts.SIMULATE}
        </Loader>
    </MenuItem>
);

export const MenuActivate = ({ classes = {}, onClick, ...rest }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.EmailArrowRightOutline className={classes.actionIcons} /> {texts.ACTIVATE}
        </ListItemText>
    </MenuItem>
);

export const MenuTest = ({ classes = {}, onClick, ...rest }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.BookmarkCheck className={classes.actionIcons} /> {texts.TEST}
        </ListItemText>
    </MenuItem>
);

export const MenuExport = ({ classes = {}, onClick, label, ...rest }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <icons.FileExportOutline className={classes.actionIcons} /> {label}
        </ListItemText>
    </MenuItem>
);

export const MenuResendInvitation = ({ classes = {}, onClick, label, loading, ...rest }) => (
    <MenuItem classes={{ root: classes.menuItem }} onClick={onClick} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={loading} marginTop={0} size={20} color="inherit">
                <icons.EmailSyncOutline className={classes.actionIcons} /> {label}
            </Loader>
        </ListItemText>
    </MenuItem>
);

export const MenuPortfolio = ({ classes = {}, investor, userId }) => {
    const [portfolioOpen, setPortfolioOpen] = useState(false);

    const { data: user, isLoading: userLoader } = useGetUserQuery(
        { id: userId },
        { skip: !userId || !portfolioOpen }
    );

    return (
        <React.Fragment>
            <MenuItem classes={{ root: classes.menuItem }} onClick={() => setPortfolioOpen(true)}>
                <ListItemText disableTypography classes={{ root: classes.itemText }}>
                    <icons.TableEye className={classes.actionIcons} /> {texts.ACCOUNT_SITUATION}
                </ListItemText>
            </MenuItem>
            <PortfolioDialog
                open={portfolioOpen}
                loading={userLoader}
                onClose={() => setPortfolioOpen(false)}
                investor={userId ? user : investor}
            />
        </React.Fragment>
    );
};

MenuDeleteUser.propTypes = {
    id: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['user', 'distributor']),
    classes: PropTypes.object,
};

MenuDelete.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuEdit.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuSubstitute.propTypes = {
    user: PropTypes.object.isRequired,
    originalPath: PropTypes.string,
    followupPath: PropTypes.string,
    connectAs: PropTypes.string,
    classes: PropTypes.object,
    itemText: PropTypes.string,
    disabled: PropTypes.bool,
};

MenuSendMessage.propTypes = {
    receiver: PropTypes.object.isRequired,
    classes: PropTypes.object,
};

MenuSendSMS.propTypes = {
    id: PropTypes.number,
    currentUserId: PropTypes.number,
    impersonatedByUserId: PropTypes.number,
    onSendSMS: PropTypes.func.isRequired,
    classes: PropTypes.object,
    disabled: PropTypes.bool,
};

MenuDetails.propTypes = {
    path: PropTypes.string.isRequired,
    classes: PropTypes.object,
};

MenuPath.propTypes = {
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    Icon: PropTypes.object.isRequired,
    classes: PropTypes.object,
};

MenuReopen.propTypes = {
    classes: PropTypes.object,
    onClick: PropTypes.func,
};

MenuDeactivate.propTypes = {
    id: PropTypes.number,
    currentUserId: PropTypes.number,
    impersonatedByUserId: PropTypes.number,
    status: PropTypes.string,
    classes: PropTypes.object,
};

MenuDownload.propTypes = {
    download: PropTypes.func.isRequired,
    downloading: PropTypes.bool,
    classes: PropTypes.object,
};

MenuSignBS.propTypes = {
    classes: PropTypes.object,
    onClick: PropTypes.func,
};

MenuSubscriptionTransfer.propTypes = {
    classes: PropTypes.object,
    maxAdmissionFee: PropTypes.number,
    subscriptionId: PropTypes.number,
};

MenuSubscriptionMove.propTypes = {
    classes: PropTypes.object,
    subscriptionId: PropTypes.number,
};

MenuMessageDetails.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuResendInvitation.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
    label: PropTypes.string,
    loading: PropTypes.bool,
};

MenuNewIndirectSubscription.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuExport.propTypes = {
    onClick: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    classes: PropTypes.object,
};

MenuActivate.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuTest.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
};

MenuSimulate.propTypes = {
    onClick: PropTypes.func.isRequired,
    classes: PropTypes.object,
    loading: PropTypes.bool,
};

MenuChangeRole.propTypes = {
    user: PropTypes.object.isRequired,
    isServicer: PropTypes.bool,
    isProvider: PropTypes.bool,
    isPromoter: PropTypes.bool,
    userSubscription: PropTypes.object,
    classes: PropTypes.object,
};

MenuPortfolio.propTypes = {
    userId: PropTypes.number,
    investor: PropTypes.object,
    classes: PropTypes.object,
};
